import { Calendar as CalendarIcon } from "lucide-react";
import { useRef, useState } from "react";
import {
  DateRangePicker as AriaDateRangePicker,
  DateRangePickerProps as AriaDateRangePickerProps,
  Button,
  DateValue,
  Dialog,
  FieldError,
  Group,
  Label,
  Popover,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { RangeCalendar } from "./Calendar";
import { DateInput } from "./DateField";
import { Description } from "./Field";
import { composeTailwindRenderProps } from "./utils";

export interface DateRangePickerProps<T extends DateValue>
  extends AriaDateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  buttonClassName?: string;
}

export function DateRangePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  buttonClassName,
  placeholder,
  ...props
}: DateRangePickerProps<T>) {
  const [focused, setFocused] = useState(false);
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  return (
    <AriaDateRangePicker
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {label && <Label>{label}</Label>}

      <Group
        className={twMerge(
          "border-theme-outline-variant flex items-center justify-between gap-x-5 rounded-sm border p-2 px-3",
          buttonClassName,
        )}
      >
        <div className="flex items-center gap-x-1">
          <DateInput
            slot="start"
            ref={startInputRef}
            className="flex flex-1 gap-0"
          />

          <span aria-hidden="true" className="font-bold">
            –
          </span>

          <DateInput
            slot="end"
            ref={endInputRef}
            className="flex flex-1 gap-0"
          />
        </div>

        <Button className="text-theme-on-background hover:bg-theme-on-background-hover relative z-1 -m-1 cursor-pointer rounded p-1 outline-offset-0">
          <CalendarIcon className="size-5" />
        </Button>
      </Group>

      {description && <Description>{description}</Description>}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}

      <Popover
        className="colors-glass backdrop-blur-glass rounded-md p-2"
        offset={8}
        placement="bottom"
      >
        <Dialog className="bg-transparent">
          <RangeCalendar />
        </Dialog>
      </Popover>
    </AriaDateRangePicker>
  );
}
